import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ChevronDown, Save, Loader2, Link as LinkIcon, Copy, Plus, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { calculateTrialCost, getCostBreakdown } from '@/utils/trialCostCalculator';
import { CLASS_TRIAL_STATUS_OPTIONS, getTrialStatusColor } from '@/components/admin/class-trials/classTrialStatusColors';
import { getMonthYearFromDate } from '@/hooks/useClassTrials';
import ReactSelect from 'react-select';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const GRADE_OPTIONS = ["ป.1","ป.2","ป.3","ป.4","ป.5","ป.6","ม.1","ม.2","ม.3","ม.4","ม.5","ม.6","มหาวิทยาลัย"];
const CLASS_TYPE_OPTIONS = ["Premiere/TH", "Premiere/Inter", "Private class/TH", "Private class/Inter"];
const LOCATION_OPTIONS = ["ออนไลน์", "งามวงศ์วาน", "บรรทัดทอง"];
const PARENT_CONTACT_CHANNELS = ["เพจ Facebook", "LINE", "โทรศัพท์", "อื่นๆ"];
const SUBJECT_OPTIONS = ["คณิตศาสตร์", "เคมี", "ชีววิทยา", "ฟิสิกส์", "ภาษาจีน", "ภาษาไทย", "วิทยาศาสตร์", "สังคม", "อังกฤษ", "อื่นๆ"];
const STATUS_OPTIONS = CLASS_TRIAL_STATUS_OPTIONS;
const CLASSROOM_TYPE_OPTIONS = ["ปกติ", "Inter", "EP"];
const LEARNING_FORMAT_OPTIONS = ["ออนไลน์", "ออนไซต์", "ออนไลน์/ออนไซต์"];
const TEACHING_STYLE_OPTIONS = [
  "ครูที่สามารถละลายพฤติกรรม",
  "สอนแบบจริงจัง เข้มงวด",
  "ครูต้องให้ Mindset ในการเรียนของน้องได้",
  "ครูที่สามารถดึงความสนใจน้องได้",
  "ครูที่มีเทคนิคการสอน",
  "ครูที่มีพลังบวก คอยชื่นชมน้อง เพิ่มความมั่นใจให้กับน้อง",
  "ใจเย็น มีเหตุผล เข้าใจธรรมชาติของผู้เรียน",
];
const PARENT_CONCERNS_OPTIONS = [
  "ไม่ค่อยพูด เป็นเด็กขี้อาย ไม่กล้าถาม",
  "ไม่ค่อยมีแพชชั่นในการเรียน",
  "โฟกัสอะไรนานๆไม่ได้ ชวนครูคุยออกนอกเรื่อง",
  "ไม่ค่อยจำ ลืมง่าย",
  "ไม่ค่อยชอบเรียนเนื้อหายากๆ",
  "ต้องการฝึกทำโจทย์ เตรียมสอบเข้า",
];
const COURSE_OPTIONS = ["Premier Class TH", "Premier Class Inter", "อื่นๆ"];
const RECORDING_STATUS_OPTIONS = ["ครูอัดจอ", "เซลอัดจอ", "โอพีอัดจอ"];
const RECOMMENDED_COURSE_OPTIONS = ["Private Class", "Premiere Class", "Premiere Class Inter"];

interface FollowUp {
  id?: string;
  followDate: string;
  detail: string;
  assignedEmployeeEmail: string;
}

interface EmployeeOption {
  value: string;
  label: string;
  email: string;
}

interface TeacherOption {
  value: string;
  label: string;
  code: string;
  name: string;
  nickname: string;
  tier: string | null;
}

function CollapsibleSection({ title, defaultOpen = true, forceOpen, children }: { title: string; defaultOpen?: boolean; forceOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="flex items-center justify-between text-base">
              {title}
              <ChevronDown className={cn("h-5 w-5 transition-transform", open && "rotate-180")} />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function DatePickerField({ label, value, onChange, error }: { label: React.ReactNode; value: string; onChange: (v: string) => void; error?: boolean }) {
  const dateValue = value ? new Date(value + 'T00:00:00') : undefined;
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground", error && "border-red-500")}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(new Date(value + 'T00:00:00'), 'dd/MM/yyyy') : 'เลือกวันที่'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={(d) => onChange(d ? format(d, 'yyyy-MM-dd') : '')}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function RatingField({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      <RadioGroup
        value={value?.toString() || ''}
        onValueChange={(v) => onChange(parseInt(v))}
        className="flex gap-3"
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="flex items-center space-x-1">
            <RadioGroupItem value={n.toString()} id={`rating-${label}-${n}`} />
            <Label htmlFor={`rating-${label}-${n}`} className="text-sm cursor-pointer">{n}</Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

export default function AdminClassTrialEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Existing fields
  const [classDate, setClassDate] = useState('');
  const [classType, setClassType] = useState('Premiere/TH');
  const [grade, setGrade] = useState('ป.1');
  const [location, setLocation] = useState('ออนไลน์');
  const [status, setStatus] = useState('OP กำลังหาครู');
  const [studentName, setStudentName] = useState('');
  const [studentNickname, setStudentNickname] = useState('');
  const [teacherCode, setTeacherCode] = useState('');
  const [trialClassroom, setTrialClassroom] = useState('');
  const [note, setNote] = useState('');
  const [failureReason, setFailureReason] = useState('');
  const [orderCode, setOrderCode] = useState('');
  const [orderId, setOrderId] = useState('');
  const [closedSuccessAt, setClosedSuccessAt] = useState('');
  const [hours, setHours] = useState(1);

  // Section 1: New fields
  const [studentFirstname, setStudentFirstname] = useState('');
  const [studentLastname, setStudentLastname] = useState('');
  const [school, setSchool] = useState('');
  const [subject, setSubject] = useState<string>('');
  const [language, setLanguage] = useState('');
  const [classroomType, setClassroomType] = useState('');
  const [learningFormat, setLearningFormat] = useState('');
  const [otherSubject, setOtherSubject] = useState('');
  const [trialDay, setTrialDay] = useState('');
  const [trialTime, setTrialTime] = useState('');
  const [trialTopic, setTrialTopic] = useState('');
  const [parentContactChannel, setParentContactChannel] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [convenientTime, setConvenientTime] = useState('');
  const [inconvenientTime, setInconvenientTime] = useState('');
  const [inconvenientReason, setInconvenientReason] = useState('');
  const [actualTrialDay, setActualTrialDay] = useState('');
  const [actualTrialTime, setActualTrialTime] = useState('');
  const [recordingStatus, setRecordingStatus] = useState('');
  const [cardLink, setCardLink] = useState('');

  // Section 2
  const [teachingStyle, setTeachingStyle] = useState<string[]>([]);
  const [parentConcerns, setParentConcerns] = useState<string[]>([]);
  const [parentConcernsOther, setParentConcernsOther] = useState('');
  const [parentExpectationsTrial, setParentExpectationsTrial] = useState('');
  const [parentExpectationsFishsix, setParentExpectationsFishsix] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');

  // Section 3
  const [desiredStartDate, setDesiredStartDate] = useState('');
  const [leadId, setLeadId] = useState('');
  const [desiredCourse, setDesiredCourse] = useState('');
  const [desiredHours, setDesiredHours] = useState<number | ''>('');
  const [consultantTeacher, setConsultantTeacher] = useState('');
  const [additionalPainPoints, setAdditionalPainPoints] = useState('');

  // Section 4: Dynamic follow-ups
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [deletedFollowUpIds, setDeletedFollowUpIds] = useState<string[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  // Section 5
  const [evaluatorTeacher, setEvaluatorTeacher] = useState('');
  const [preScoreReason, setPreScoreReason] = useState('');
  const [postScoreReason, setPostScoreReason] = useState('');
  const [trialOverview, setTrialOverview] = useState('');
  const [studentStrengths, setStudentStrengths] = useState('');
  const [studentImprovements, setStudentImprovements] = useState('');
  const [teachingApproach, setTeachingApproach] = useState('');
  const [recommendedDuration, setRecommendedDuration] = useState('');
  const [recommendedCourse, setRecommendedCourse] = useState('');

  // Teacher acknowledgement (read-only on admin)
  const [teacherAcknowledged, setTeacherAcknowledged] = useState(false);

  // Section 6
  const [rating11, setRating11] = useState<number | null>(null);
  const [rating12, setRating12] = useState<number | null>(null);
  const [rating13, setRating13] = useState<number | null>(null);
  const [rating21, setRating21] = useState<number | null>(null);
  const [rating22, setRating22] = useState<number | null>(null);
  const [rating23, setRating23] = useState<number | null>(null);
  const [rating31, setRating31] = useState<number | null>(null);
  const [rating32, setRating32] = useState<number | null>(null);
  const [rating33, setRating33] = useState<number | null>(null);
  const [rating41, setRating41] = useState<number | null>(null);
  const [rating42, setRating42] = useState<number | null>(null);
  const [rating43, setRating43] = useState<number | null>(null);

  // Specialized tutoring
  const [specializedTutoring, setSpecializedTutoring] = useState<string[]>([]);
  const [specializedTutoringOptions, setSpecializedTutoringOptions] = useState<string[]>([]);

  // Teacher search
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherOption | null>(null);
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('');
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);

  // Order search
  const [orderSearchOptions, setOrderSearchOptions] = useState<{ value: string; label: string; orderId: string; orderCode: string }[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<{ value: string; label: string; orderId: string; orderCode: string } | null>(null);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  // Load trial data
  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('class_trials')
        .select('*')
        .eq('id', id)
        .single();
      if (error || !data) {
        toast({ title: 'ไม่พบข้อมูล', variant: 'destructive' });
        navigate('/admin/class-trials');
        return;
      }
      const d = data as any;
      setClassDate(d.class_date || '');
      setClassType(d.class_type || 'Premiere/TH');
      setGrade(d.grade || 'ป.1');
      setLocation(d.location || 'ออนไลน์');
      setStatus(d.status || '');
      setStudentName(d.student_name || '');
      setStudentNickname(d.student_nickname || '');
      setTeacherCode(d.teacher_code || '');
      setTrialClassroom((d as any).trial_classroom || '');
      setNote(d.note || '');
      setFailureReason((d as any).failure_reason || '');
      setOrderCode(d.order_code || '');
      setOrderId(d.order_id || '');
      setClosedSuccessAt(d.closed_success_at ? d.closed_success_at.split('T')[0] : '');
      if (d.order_code) {
        setSelectedOrder({ value: d.order_code, label: d.order_code, orderId: d.order_id || '', orderCode: d.order_code });
      }
      setHours(d.hours ?? 1);
      // New fields
      setStudentFirstname(d.student_firstname || '');
      setStudentLastname(d.student_lastname || '');
      setSchool(d.school || '');
      setSubject(d.subject || '');
      setLanguage(d.language || '');
      setClassroomType(d.classroom_type || '');
      setLearningFormat(d.learning_format || '');
      setOtherSubject(d.other_subject || '');
      const trialTimeFull = d.trial_time || '';
      // Try to parse as "YYYY-MM-DD rest" format first, fallback to old split
      const dateMatch = trialTimeFull.match(/^(\d{4}-\d{2}-\d{2})\s*(.*)?$/);
      if (dateMatch) {
        setTrialDay(dateMatch[1]);
        setTrialTime(dateMatch[2] || '');
      } else {
        const trialTimeParts = trialTimeFull.split(' ');
        setTrialDay(trialTimeParts.length > 1 ? trialTimeParts[0] : '');
        setTrialTime(trialTimeParts.length > 1 ? trialTimeParts.slice(1).join(' ') : trialTimeFull);
      }
      setTrialTopic(d.trial_topic || '');
      setTeachingStyle(d.teaching_style || []);
      // Parse parent_concerns: try JSON array first, fallback to plain text
      const rawConcerns = d.parent_concerns || '';
      try {
        const parsed = JSON.parse(rawConcerns);
        if (Array.isArray(parsed)) {
          const knownConcerns = parsed.filter((c: string) => PARENT_CONCERNS_OPTIONS.includes(c));
          const otherConcerns = parsed.filter((c: string) => !PARENT_CONCERNS_OPTIONS.includes(c));
          setParentConcerns(knownConcerns);
          setParentConcernsOther(otherConcerns.join(', '));
        } else {
          setParentConcerns([]);
          setParentConcernsOther(rawConcerns);
        }
      } catch {
        // Plain text - put it in "other"
        setParentConcerns([]);
        setParentConcernsOther(rawConcerns);
      }
      setParentExpectationsTrial(d.parent_expectations_trial || '');
      setParentExpectationsFishsix(d.parent_expectations_fishsix || '');
      setSuccessCriteria(d.success_criteria || '');
      setParentContactChannel(d.parent_contact_channel || '');
      setParentName(d.parent_name || '');
      setParentPhone(d.parent_phone || '');
      setStudentPhone((d as any).student_phone || '');
      setConvenientTime(d.convenient_time || '');
      setInconvenientTime(d.inconvenient_time || '');
      setInconvenientReason(d.inconvenient_reason || '');
      setActualTrialDay(d.actual_trial_day || '');
      setActualTrialTime(d.actual_trial_time || '');
      setRecordingStatus(d.recording_status || '');
      setCardLink(d.card_link || '');
      setDesiredStartDate(d.desired_start_date || '');
      setLeadId(d.lead_id || '');
      setDesiredCourse(d.desired_course || '');
      setDesiredHours(d.desired_hours ?? '');
      setConsultantTeacher(d.consultant_teacher || '');
      setAdditionalPainPoints(d.additional_pain_points || '');
      // Load follow-ups from new table
      const { data: followUpData } = await supabase
        .from('class_trial_followups')
        .select('*')
        .eq('class_trial_id', id)
        .order('sort_order');
      if (followUpData && followUpData.length > 0) {
        setFollowUps(followUpData.map((f: any) => ({
          id: f.id,
          followDate: f.follow_date || '',
          detail: f.detail || '',
          assignedEmployeeEmail: f.assigned_employee_email || '',
        })));
      }
      setEvaluatorTeacher(d.evaluator_teacher || '');
      setTeacherAcknowledged(d.teacher_acknowledged || false);
      setPreScoreReason(d.pre_score_reason || '');
      setPostScoreReason(d.post_score_reason || '');
      setTrialOverview(d.trial_overview || '');
      setStudentStrengths(d.student_strengths || '');
      setStudentImprovements(d.student_improvements || '');
      setTeachingApproach(d.teaching_approach || '');
      setRecommendedDuration(d.recommended_duration || '');
      setRecommendedCourse(d.recommended_course || '');
      // Parse specialized_tutoring JSON
      try {
        const parsed = JSON.parse((d as any).specialized_tutoring || '[]');
        if (Array.isArray(parsed)) setSpecializedTutoring(parsed);
      } catch { setSpecializedTutoring([]); }
      setRating11(d.rating_1_1);
      setRating12(d.rating_1_2);
      setRating13(d.rating_1_3);
      setRating21(d.rating_2_1);
      setRating22(d.rating_2_2);
      setRating23(d.rating_2_3);
      setRating31(d.rating_3_1);
      setRating32(d.rating_3_2);
      setRating33(d.rating_3_3);
      setRating41(d.rating_4_1);
      setRating42(d.rating_4_2);
      setRating43(d.rating_4_3);
      setIsLoading(false);
    };
    load();
  }, [id, isNew, navigate]);

  // Fetch specialized tutoring options
  useEffect(() => {
    const fetchOptions = async () => {
      const { data } = await supabase.from('specialized_tutorings').select('name').order('name');
      if (data) setSpecializedTutoringOptions(data.map((d: any) => d.name));
    };
    fetchOptions();
  }, []);

  // Teacher search
  useEffect(() => {
    const search = async () => {
      if (teacherSearchQuery.length < 1 && !teacherCode) return;
      setIsLoadingTeachers(true);
      try {
        const { data } = await supabase.functions.invoke('search-teachers', {
          body: { query: teacherSearchQuery || teacherCode },
        });
        const options = (data?.teachers || []).map((t: any) => ({
          value: t.code,
          label: `${t.code} - ${t.name} (${t.nickname || ''})`,
          code: t.code,
          name: t.name,
          nickname: t.nickname || '',
          tier: t.tier,
        }));
        setTeachers(options);
        if (teacherCode && !selectedTeacher) {
          const found = options.find((o: TeacherOption) => o.value === teacherCode);
          if (found) setSelectedTeacher(found);
        }
      } catch (e) {
        console.error('Error searching teachers:', e);
      } finally {
        setIsLoadingTeachers(false);
      }
    };
    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [teacherSearchQuery, teacherCode, selectedTeacher]);

  // Order search
  useEffect(() => {
    if (status !== 'ปิดการขายเรียบร้อย') return;
    const search = async () => {
      if (orderSearchQuery.length < 2) return;
      setIsLoadingOrders(true);
      try {
        const { data } = await supabase
          .from('order_overview')
          .select('id, order_code, customer_name')
          .or(`order_code.ilike.%${orderSearchQuery}%,customer_name.ilike.%${orderSearchQuery}%`)
          .limit(20);
        const options = (data || []).map((o: any) => ({
          value: o.order_code,
          label: `${o.order_code} — ${o.customer_name || ''}`,
          orderId: o.id,
          orderCode: o.order_code,
        }));
        setOrderSearchOptions(options);
      } catch (e) {
        console.error('Error searching orders:', e);
      } finally {
        setIsLoadingOrders(false);
      }
    };
    const debounce = setTimeout(search, 400);
    return () => clearTimeout(debounce);
  }, [orderSearchQuery, status]);

  // Employee search for follow-up assignment
  const searchEmployees = async (query: string) => {
    if (query.length < 1) return;
    setIsLoadingEmployees(true);
    try {
      const { data } = await supabase
        .from('employees' as any)
        .select('first_name, last_name, nickname, email')
        .or(`first_name.ilike.%${query}%,last_name.ilike.%${query}%,nickname.ilike.%${query}%`)
        .limit(20);
      const options = (data || []).map((e: any) => ({
        value: e.email,
        label: `${e.first_name} ${e.last_name || ''} (${e.nickname || ''})`,
        email: e.email,
      }));
      setEmployeeOptions(options);
    } catch (e) {
      console.error('Error searching employees:', e);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  const updateFollowUp = (index: number, field: keyof FollowUp, value: string) => {
    setFollowUps(prev => prev.map((fu, i) => i === index ? { ...fu, [field]: value } : fu));
  };

  const addFollowUp = () => {
    setFollowUps(prev => [...prev, { followDate: '', detail: '', assignedEmployeeEmail: '' }]);
  };

  const removeFollowUp = (index: number) => {
    const fu = followUps[index];
    if (fu.id) setDeletedFollowUpIds(prev => [...prev, fu.id!]);
    setFollowUps(prev => prev.filter((_, i) => i !== index));
  };
  const clearError = (field: string) => {
    setValidationErrors(prev => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const validateForm = (): boolean => {
    const errors = new Set<string>();
    const requiredFields: [string, any][] = [
      ['studentFirstname', studentFirstname],
      ['studentLastname', studentLastname],
      ['studentNickname', studentNickname],
      ['grade', grade],
      ['classroomType', classroomType],
      ['school', school],
      ['subject', subject],
      ['language', language],
      ['learningFormat', learningFormat],
      ['classDate', classDate],
      ['trialTime', trialTime],
      ['recordingStatus', recordingStatus],
      ['convenientTime', convenientTime],
      ['inconvenientTime', inconvenientTime],
      ['inconvenientReason', inconvenientReason],
      ['parentContactChannel', parentContactChannel],
      ['parentName', parentName],
      ['parentPhone', parentPhone],
      ['trialTopic', trialTopic],
      ['classType', classType],
      ['location', location],
      ['teacherCode', teacherCode],
      ['consultantTeacher', consultantTeacher],
    ];
    for (const [name, value] of requiredFields) {
      if (!value || (typeof value === 'string' && !value.trim())) {
        errors.add(name);
      }
    }
    // Conditionally require failure_reason
    if (status === 'ปิดการขายไม่สำเร็จ' && !failureReason.trim()) {
      errors.add('failureReason');
    }

    setValidationErrors(errors);
    if (errors.size > 0) {
      toast({ title: 'กรุณากรอกข้อมูลให้ครบ', description: 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนดำเนินการต่อ', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    // Validation disabled — allow save regardless of missing required fields
    setValidationErrors(new Set());
    setIsSaving(true);
    try {
      // Auto-fill closedSuccessAt if status is "ปิดการขาย" and not yet set
      let finalClosedSuccessAt = closedSuccessAt;
      if ((status === 'ปิดการขายเรียบร้อย' || status === 'ปิดการขายไม่สำเร็จ') && !closedSuccessAt) {
        finalClosedSuccessAt = format(new Date(), 'yyyy-MM-dd');
        setClosedSuccessAt(finalClosedSuccessAt);
      }
      const hasCommission = status === 'ปิดการขายเรียบร้อย';
      const cost = calculateTrialCost(grade, classType, location, hasCommission, selectedTeacher?.tier || null, hours);
      const { month, year } = classDate
        ? getMonthYearFromDate(classDate)
        : (() => {
            const now = new Date();
            return { month: now.getMonth() + 1, year: now.getFullYear() };
          })();

      const payload: any = {
        class_date: classDate || null,
        class_type: classType,
        grade,
        location,
        status,
        student_name: `${studentFirstname} ${studentLastname}`.trim() || studentName || null,
        student_nickname: studentNickname,
        teacher_code: teacherCode,
        note,
        order_code: orderCode || null,
        order_id: orderId || null,
        closed_success_at: finalClosedSuccessAt ? new Date(finalClosedSuccessAt + 'T00:00:00').toISOString() : null,
        hours,
        cost,
        month,
        year,
        student_firstname: studentFirstname || null,
        student_lastname: studentLastname || null,
        school: school || null,
        subject: (subject === 'อื่นๆ' ? otherSubject : subject) || null,
        language: language || null,
        classroom_type: classroomType || null,
        trial_classroom: trialClassroom || null,
        learning_format: learningFormat || null,
        other_subject: otherSubject || null,
        trial_time: [trialDay.trim(), trialTime.trim()].filter(Boolean).join(' ') || null,
        trial_topic: trialTopic || null,
        teaching_style: teachingStyle.length > 0 ? teachingStyle : null,
        parent_concerns: (() => {
          const allConcerns = [...parentConcerns];
          if (parentConcernsOther.trim()) allConcerns.push(parentConcernsOther.trim());
          return allConcerns.length > 0 ? JSON.stringify(allConcerns) : null;
        })(),
        parent_expectations_trial: parentExpectationsTrial || null,
        parent_expectations_fishsix: parentExpectationsFishsix || null,
        success_criteria: successCriteria || null,
        parent_contact_channel: parentContactChannel || null,
        parent_name: parentName || null,
        parent_phone: parentPhone || null,
        student_phone: studentPhone || null,
        convenient_time: convenientTime.trim() || null,
        inconvenient_time: inconvenientTime || null,
        inconvenient_reason: inconvenientReason || null,
        actual_trial_day: actualTrialDay || null,
        actual_trial_time: actualTrialTime || null,
        recording_status: recordingStatus || null,
        card_link: cardLink || null,
        failure_reason: failureReason || null,
        desired_start_date: desiredStartDate || null,
        desired_schedule: null,
        lead_id: leadId || null,
        desired_course: desiredCourse || null,
        desired_hours: desiredHours || null,
        consultant_teacher: consultantTeacher || null,
        additional_pain_points: additionalPainPoints || null,
        evaluator_teacher: evaluatorTeacher || null,
        pre_score_reason: preScoreReason || null,
        post_score_reason: postScoreReason || null,
        trial_overview: trialOverview || null,
        student_strengths: studentStrengths || null,
        student_improvements: studentImprovements || null,
        teaching_approach: teachingApproach || null,
        recommended_duration: recommendedDuration || null,
        recommended_course: recommendedCourse || null,
        specialized_tutoring: specializedTutoring.length > 0 ? JSON.stringify(specializedTutoring) : null,
        rating_1_1: rating11,
        rating_1_2: rating12,
        rating_1_3: rating13,
        rating_2_1: rating21,
        rating_2_2: rating22,
        rating_2_3: rating23,
        rating_3_1: rating31,
        rating_3_2: rating32,
        rating_3_3: rating33,
        rating_4_1: rating41,
        rating_4_2: rating42,
        rating_4_3: rating43,
      };

      let savedTrialId = id;
      if (isNew) {
        const { data: insertData, error: insertError } = await supabase.from('class_trials').insert(payload).select('id').single();
        if (insertError) throw insertError;
        savedTrialId = insertData.id;
      } else {
        const { error: updateError } = await supabase.from('class_trials').update(payload).eq('id', id);
        if (updateError) throw updateError;
      }

      // Save follow-ups
      // Delete removed ones
      if (deletedFollowUpIds.length > 0) {
        await supabase.from('class_trial_followups').delete().in('id', deletedFollowUpIds);
      }
      // Upsert existing and new
      for (let i = 0; i < followUps.length; i++) {
        const fu = followUps[i];
        const fuPayload: any = {
          class_trial_id: savedTrialId,
          follow_date: fu.followDate || null,
          detail: fu.detail || null,
          assigned_employee_email: fu.assignedEmployeeEmail || null,
          sort_order: i,
        };
        if (fu.id) {
          await supabase.from('class_trial_followups').update(fuPayload).eq('id', fu.id);
        } else {
          await supabase.from('class_trial_followups').insert(fuPayload);
        }
      }

      // Create Google Calendar events for follow-ups with assigned employees
      for (const fu of followUps) {
        if (fu.assignedEmployeeEmail && fu.followDate) {
          try {
            await supabase.functions.invoke('create-calendar-event', {
              body: {
                summary: `ติดตามผลทดลองเรียน - ${studentNickname || studentName}`,
                description: fu.detail || '',
                date: fu.followDate,
                attendeeEmail: fu.assignedEmployeeEmail,
                studentName: studentName || studentNickname || '',
              },
            });
          } catch (calErr) {
            console.error('Calendar event error:', calErr);
          }
        }
      }

      toast({ title: 'บันทึกสำเร็จ' });
      navigate('/admin/class-trials');
    } catch (e) {
      console.error('Save error:', e);
      toast({ title: 'เกิดข้อผิดพลาด', description: 'ไม่สามารถบันทึกได้', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AdminSidebar />
          <main className="flex-1 p-6 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <main className="flex-1 p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/class-trials')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-bold flex-1">
                {isNew ? 'เพิ่มทดลองเรียน' : 'แก้ไขข้อมูลทดลองเรียน'}
              </h1>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const text = [
                    `ชื่อจริง: ${studentFirstname}`,
                    `นามสกุล: ${studentLastname}`,
                    `ชื่อเล่น: ${studentNickname}`,
                    `ระดับชั้นเรียน: ${grade}`,
                    `วิชาที่ต้องการทดลองเรียน: ${subject === 'อื่นๆ' ? otherSubject : subject}`,
                    `ระบุทั้งเนื้อหาและภาษาที่สื่อสาร: ${language}`,
                    `รูปแบบการเรียน: ${learningFormat}`,
                    `วันที่ต้องการทดลองเรียน: ${trialDay ? format(new Date(trialDay + 'T00:00:00'), 'dd/MM/yyyy') : ''}`,
                    `เวลาที่ต้องการทดลองเรียน: ${trialTime}`,
                    `วันที่ทดลองเรียนจริง: ${actualTrialDay ? format(new Date(actualTrialDay + 'T00:00:00'), 'dd/MM/yyyy') : ''}`,
                    `เวลาที่ทดลองเรียนจริง: ${actualTrialTime}`,
                    `สถานะการอัดจอ: ${recordingStatus}`,
                    `สถานะ: ${status}`,
                    `ลิงก์การ์ด: ${cardLink}`,
                  ].join('\n');
                  navigator.clipboard.writeText(text);
                  toast({ title: 'คัดลอกข้อมูลแล้ว' });
                }}
              >
                <Copy className="h-4 w-4 mr-1" />
                คัดลอก
              </Button>
            </div>

            {/* Status Card — top of form */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">สถานะและหมายเหตุ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>สถานะ *</Label>
                    <Select value={status} onValueChange={(newStatus) => {
                      setStatus(newStatus);
                      if ((newStatus === 'ปิดการขายเรียบร้อย' || newStatus === 'ปิดการขายไม่สำเร็จ') && !closedSuccessAt) {
                        setClosedSuccessAt(format(new Date(), 'yyyy-MM-dd'));
                      }
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>
                            <span className={`inline-block px-2 py-0.5 rounded text-xs border ${getTrialStatusColor(s)}`}>{s}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>หมายเหตุ</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="หมายเหตุเพิ่มเติม..." />
                  </div>
                </div>

                {/* Failure reason — show when ปิดการขายไม่สำเร็จ */}
                {status === 'ปิดการขายไม่สำเร็จ' && (
                  <div className="space-y-2">
                    <Label>ปิดการขายไม่สำเร็จเพราะอะไร <span className="text-red-500">*</span></Label>
                    <Textarea
                      value={failureReason}
                      onChange={(e) => { setFailureReason(e.target.value); clearError('failureReason'); }}
                      rows={2}
                      placeholder="ระบุเหตุผลที่ปิดการขายไม่สำเร็จ..."
                      className={cn(validationErrors.has('failureReason') && 'border-red-500')}
                    />
                  </div>
                )}

                {/* Order Linking — show when ปิดการขายเรียบร้อย */}
                {(status === 'ปิดการขายเรียบร้อย' || status === 'ปิดการขายไม่สำเร็จ') && (
                  <div className="border-t pt-4 space-y-4">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      เชื่อมต่อคำสั่งซื้อ
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>ค้นหา Order</Label>
                        <ReactSelect
                          value={selectedOrder}
                          onChange={(opt) => {
                            setSelectedOrder(opt);
                            setOrderCode(opt?.orderCode || '');
                            setOrderId(opt?.orderId || '');
                          }}
                          onInputChange={setOrderSearchQuery}
                          options={orderSearchOptions}
                          isLoading={isLoadingOrders}
                          placeholder="พิมพ์ order code หรือชื่อลูกค้า..."
                          isClearable
                          noOptionsMessage={() => orderSearchQuery.length < 2 ? 'พิมพ์อย่างน้อย 2 ตัวอักษร' : 'ไม่พบ order'}
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>
                      <DatePickerField label="วันที่ปิดการขาย" value={closedSuccessAt} onChange={setClosedSuccessAt} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <CollapsibleSection title="ข้อมูลการทดลองเรียน" forceOpen={validationErrors.size > 0}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                  <Label>ชื่อจริงนักเรียน <span className="text-red-500">*</span></Label>
                  <Input value={studentFirstname} onChange={(e) => { setStudentFirstname(e.target.value); clearError('studentFirstname'); }} placeholder="ชื่อจริง" className={cn(validationErrors.has('studentFirstname') && 'border-red-500')} />
                </div>
                <div className="space-y-2">
                  <Label>นามสกุลนักเรียน <span className="text-red-500">*</span></Label>
                  <Input value={studentLastname} onChange={(e) => { setStudentLastname(e.target.value); clearError('studentLastname'); }} placeholder="นามสกุล" className={cn(validationErrors.has('studentLastname') && 'border-red-500')} />
                </div>
                <div className="space-y-2">
                  <Label>ชื่อเล่นนักเรียน <span className="text-red-500">*</span></Label>
                  <Input value={studentNickname} onChange={(e) => { setStudentNickname(e.target.value); clearError('studentNickname'); }} placeholder="ชื่อเล่น" className={cn(validationErrors.has('studentNickname') && 'border-red-500')} />
                </div>
                <div className="space-y-2">
                  <Label>ระดับชั้นเรียน <span className="text-red-500">*</span></Label>
                  <Select value={grade} onValueChange={(v) => { setGrade(v); clearError('grade'); }}>
                    <SelectTrigger className={cn(validationErrors.has('grade') && 'border-red-500')}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GRADE_OPTIONS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ประเภทห้องเรียน <span className="text-red-500">*</span></Label>
                  <RadioGroup value={classroomType} onValueChange={(v) => { setClassroomType(v); clearError('classroomType'); }} className={cn("flex gap-4", validationErrors.has('classroomType') && 'border border-red-500 rounded-md p-2')}>
                    {CLASSROOM_TYPE_OPTIONS.map((o) => (
                      <div key={o} className="flex items-center space-x-1">
                        <RadioGroupItem value={o} id={`ct-${o}`} />
                        <Label htmlFor={`ct-${o}`} className="cursor-pointer text-sm">{o}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label>โรงเรียน <span className="text-red-500">*</span></Label>
                  <Input value={school} onChange={(e) => { setSchool(e.target.value); clearError('school'); }} placeholder="ชื่อโรงเรียน" className={cn(validationErrors.has('school') && 'border-red-500')} />
                </div>
                <div className={cn("md:col-span-2 space-y-2", validationErrors.has('subject') && 'border border-red-500 rounded-md p-2')}>
                  <Label>วิชาที่ต้องการทดลองเรียน <span className="text-red-500">*</span></Label>
                  <Select
                    value={subject}
                    onValueChange={(v) => { setSubject(v); clearError('subject'); }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกวิชา" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECT_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {subject === "อื่นๆ" && (
                    <div className="space-y-2 mt-3">
                      <Label>ระบุวิชา</Label>
                      <Textarea value={otherSubject} onChange={(e) => setOtherSubject(e.target.value)} placeholder="กรอกวิชาที่ต้องการทดลองเรียนเพิ่มเติม" rows={2} />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>ระบุทั้งเนื้อหาและภาษาที่สื่อสาร <span className="text-red-500">*</span></Label>
                  <Input value={language} onChange={(e) => { setLanguage(e.target.value); clearError('language'); }} placeholder="เช่น เนื้อหาอังกฤษ สื่อสารไทย / เนื้อหาไทยสื่อสารไทย" className={cn(validationErrors.has('language') && 'border-red-500')} />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>ต้องการเรียนเฉพาะทาง</Label>
                  <p className="text-xs text-muted-foreground mb-1">เลือกได้มากกว่า 1 ข้อ</p>
                  <ReactSelect
                    isMulti
                    options={specializedTutoringOptions.map((o) => ({ value: o, label: o }))}
                    value={specializedTutoring.map((v) => ({ value: v, label: v }))}
                    onChange={(selected) => {
                      setSpecializedTutoring(selected ? selected.map((s: any) => s.value) : []);
                    }}
                    placeholder="เลือกเฉพาะทาง..."
                    noOptionsMessage={() => "ไม่พบตัวเลือก"}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ ...base, minHeight: '40px', borderColor: 'hsl(var(--border))', borderRadius: 'calc(var(--radius) - 2px)' }),
                      menu: (base) => ({ ...base, zIndex: 50 }),
                    }}
                  />
                  <Label>รูปแบบการเรียน <span className="text-red-500">*</span></Label>
                  <Select value={learningFormat} onValueChange={(v) => { setLearningFormat(v); clearError('learningFormat'); }}>
                    <SelectTrigger className={cn(validationErrors.has('learningFormat') && 'border-red-500')}><SelectValue placeholder="เลือกรูปแบบ" /></SelectTrigger>
                    <SelectContent>
                      {LEARNING_FORMAT_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* ทดลองเรียน Section */}
                <div className="md:col-span-2 space-y-3 rounded-lg border border-border p-4">
                  <h4 className="font-semibold text-foreground">ทดลองเรียน</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <DatePickerField label={<>วันที่ทดลองเรียน <span className="text-red-500">*</span></>} value={classDate} onChange={(v) => { setClassDate(v); clearError('classDate'); }} error={validationErrors.has('classDate')} />
                    <div className="space-y-2">
                      <Label>เวลาที่ต้องการทดลองเรียน <span className="text-red-500">*</span></Label>
                      <Input value={trialTime} onChange={(e) => { setTrialTime(e.target.value); clearError('trialTime'); }} placeholder="เช่น 10:00 - 12:00" className={cn(validationErrors.has('trialTime') && 'border-red-500')} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label>สถานะการอัดจอ <span className="text-red-500">*</span></Label>
                      <Select value={recordingStatus} onValueChange={(v) => { setRecordingStatus(v); clearError('recordingStatus'); }}>
                        <SelectTrigger className={cn(validationErrors.has('recordingStatus') && 'border-red-500')}><SelectValue placeholder="เลือกสถานะ" /></SelectTrigger>
                        <SelectContent>
                          {RECORDING_STATUS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* เวลาเรียนจริง Section */}
                <div className="md:col-span-2 space-y-3 rounded-lg border border-border p-4">
                  <h4 className="font-semibold text-foreground">เวลาเรียนจริง</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DatePickerField label="วันที่ต้องการเริ่มเรียนจริง" value={desiredStartDate} onChange={setDesiredStartDate} />
                    <div className="space-y-2">
                      <Label>วันและเวลาที่ต้องการเรียนจริง <span className="text-red-500">*</span></Label>
                      <Input value={convenientTime} onChange={(e) => { setConvenientTime(e.target.value); clearError('convenientTime'); }} placeholder="เช่น วันพุธ เวลา 11.00 - 13.00 น." className={cn(validationErrors.has('convenientTime') && 'border-red-500')} />
                    </div>
                    <div className="space-y-2">
                      <Label>ช่วงเวลาที่ไม่สะดวก <span className="text-red-500">*</span></Label>
                      <Input value={inconvenientTime} onChange={(e) => { setInconvenientTime(e.target.value); clearError('inconvenientTime'); }} placeholder="เช่น วันพุธ ทั้งวัน" className={cn(validationErrors.has('inconvenientTime') && 'border-red-500')} />
                    </div>
                    <div className="space-y-2">
                      <Label>เหตุผลที่ไม่สะดวก <span className="text-red-500">*</span></Label>
                      <Input value={inconvenientReason} onChange={(e) => { setInconvenientReason(e.target.value); clearError('inconvenientReason'); }} placeholder="เช่น มีเรียนพิเศษ" className={cn(validationErrors.has('inconvenientReason') && 'border-red-500')} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ช่องทางติดต่อผู้ปกครอง <span className="text-red-500">*</span></Label>
                  <Select value={parentContactChannel} onValueChange={(v) => { setParentContactChannel(v); clearError('parentContactChannel'); }}>
                    <SelectTrigger className={cn(validationErrors.has('parentContactChannel') && 'border-red-500')}><SelectValue placeholder="เลือกช่องทาง" /></SelectTrigger>
                    <SelectContent>
                      {PARENT_CONTACT_CHANNELS.map((ch) => <SelectItem key={ch} value={ch}>{ch}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ชื่อผู้ปกครองที่ทักเข้ามา <span className="text-red-500">*</span></Label>
                  <Input value={parentName} onChange={(e) => { setParentName(e.target.value); clearError('parentName'); }} placeholder="ชื่อผู้ปกครอง" className={cn(validationErrors.has('parentName') && 'border-red-500')} />
                </div>
                <div className="space-y-2">
                  <Label>เบอร์โทรผู้ปกครอง <span className="text-red-500">*</span></Label>
                  <Input type="tel" value={parentPhone} onChange={(e) => { setParentPhone(e.target.value); clearError('parentPhone'); }} placeholder="0xx-xxx-xxxx" className={cn(validationErrors.has('parentPhone') && 'border-red-500')} />
                </div>
                <div className="space-y-2">
                  <Label>เบอร์โทรติดต่อนักเรียน</Label>
                  <Input type="tel" value={studentPhone} onChange={(e) => setStudentPhone(e.target.value)} placeholder="0xx-xxx-xxxx" />
                </div>
                <div className="space-y-2">
                  <Label>หัวข้อที่น้องต้องการทดลองเรียน <span className="text-red-500">*</span></Label>
                  <Input value={trialTopic} onChange={(e) => { setTrialTopic(e.target.value); clearError('trialTopic'); }} placeholder="เช่น Algebra" className={cn(validationErrors.has('trialTopic') && 'border-red-500')} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ประเภทเรียน <span className="text-red-500">*</span></Label>
                    <RadioGroup value={classType} onValueChange={(v) => { setClassType(v); clearError('classType'); }} className={cn("flex flex-wrap gap-4", validationErrors.has('classType') && 'border border-red-500 rounded-md p-2')}>
                      {CLASS_TYPE_OPTIONS.map((t) => (
                        <div key={t} className="flex items-center space-x-2">
                          <RadioGroupItem value={t} id={`type-${t}`} />
                          <Label htmlFor={`type-${t}`} className="cursor-pointer">{t}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label>สถานที่ <span className="text-red-500">*</span></Label>
                    <RadioGroup value={location} onValueChange={(v) => { setLocation(v); clearError('location'); }} className={cn("flex flex-wrap gap-4", validationErrors.has('location') && 'border border-red-500 rounded-md p-2')}>
                      {LOCATION_OPTIONS.map((l) => (
                        <div key={l} className="flex items-center space-x-2">
                          <RadioGroupItem value={l} id={`loc-${l}`} />
                          <Label htmlFor={`loc-${l}`} className="cursor-pointer">{l}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2 min-w-0">
                    <Label>ครู <span className="text-red-500">*</span></Label>
                    <ReactSelect
                      value={selectedTeacher}
                      onChange={(opt) => {
                        setSelectedTeacher(opt);
                        setTeacherCode(opt?.value || '');
                        clearError('teacherCode');
                      }}
                      onInputChange={setTeacherSearchQuery}
                      options={teachers}
                      isLoading={isLoadingTeachers}
                      placeholder="ค้นหาครู..."
                      isClearable
                      noOptionsMessage={() => 'ไม่พบครู'}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={validationErrors.has('teacherCode') ? { control: (base: any) => ({ ...base, borderColor: '#ef4444' }) } : undefined}
                    />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label>ห้องเรียนสำหรับทดลองเรียน</Label>
                    <Input value={trialClassroom} onChange={(e) => setTrialClassroom(e.target.value)} placeholder="เช่น ห้อง A1, ห้อง 201" />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label>จำนวนชั่วโมง <span className="text-red-500">*</span></Label>
                    <RadioGroup value={String(hours)} onValueChange={(v) => setHours(parseFloat(v))} className="grid grid-cols-2 gap-2 pt-1">
                      {[0.5, 1, 1.5, 2, 2.5, 3].map((h) => (
                        <div key={h} className="flex items-center space-x-1">
                          <RadioGroupItem value={String(h)} id={`h-${h}`} />
                          <Label htmlFor={`h-${h}`} className="cursor-pointer text-sm">{h} ชม.</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Cost Breakdown Panel */}
            {(() => {
              const hasCommission = status === 'ปิดการขายเรียบร้อย';
              const breakdown = getCostBreakdown(grade, classType, location, hasCommission, selectedTeacher?.tier || null, hours);
              return (
                <div className="rounded-lg bg-muted p-4 space-y-2">
                  <h3 className="font-semibold text-sm">รายละเอียดค่าใช้จ่าย (คำนวณอัตโนมัติ)</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>ค่าสอนพื้นฐาน</span>
                      <span>{breakdown.base} บาท/ชม.</span>
                    </div>
                    {breakdown.gradeRate > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>+ ค่าระดับชั้น ({breakdown.gradeLabel})</span>
                        <span>+{breakdown.gradeRate} บาท/ชม.</span>
                      </div>
                    )}
                    {breakdown.classTypeRate > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>+ ค่าประเภทคลาส ({breakdown.classTypeLabel})</span>
                        <span>+{breakdown.classTypeRate} บาท/ชม.</span>
                      </div>
                    )}
                    {breakdown.locationRate > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>+ ค่าสถานที่ ({breakdown.locationLabel})</span>
                        <span>+{breakdown.locationRate} บาท/ชม.</span>
                      </div>
                    )}
                    {breakdown.tierRate > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>+ ค่า Tier ครู ({breakdown.tierLabel})</span>
                        <span>+{breakdown.tierRate} บาท/ชม.</span>
                      </div>
                    )}
                    {breakdown.hours !== 1 && (
                      <div className="flex justify-between text-blue-600">
                        <span>× จำนวนชั่วโมง</span>
                        <span>×{breakdown.hours}</span>
                      </div>
                    )}
                    {breakdown.commission > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>+ Commission (ปิดการขาย)</span>
                        <span>+{breakdown.commission} บาท</span>
                      </div>
                    )}
                    <div className="border-t pt-1 flex justify-between font-bold">
                      <span>รวมทั้งหมด</span>
                      <span>{breakdown.total} บาท</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Section 2: ความคาดหวังและปัญหา */}
            <CollapsibleSection title="ความคาดหวังและปัญหา" defaultOpen={false}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>สไตล์การสอนของคุณครู</Label>
                  <div className="space-y-2">
                    {TEACHING_STYLE_OPTIONS.map((style) => (
                      <div key={style} className="flex items-center space-x-2">
                        <Checkbox
                          id={`style-${style}`}
                          checked={teachingStyle.includes(style)}
                          onCheckedChange={(checked) => {
                            if (checked) setTeachingStyle([...teachingStyle, style]);
                            else setTeachingStyle(teachingStyle.filter((s) => s !== style));
                          }}
                        />
                        <Label htmlFor={`style-${style}`} className="text-sm cursor-pointer">{style}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ปัญหาที่ผู้ปกครองเป็นกังวล</Label>
                  <p className="text-xs text-muted-foreground mb-1">เลือกได้มากกว่า 1 ข้อ</p>
                  <div className="space-y-2">
                    {PARENT_CONCERNS_OPTIONS.map((concern) => (
                      <div key={concern} className="flex items-center space-x-2">
                        <Checkbox
                          id={`concern-${concern}`}
                          checked={parentConcerns.includes(concern)}
                          onCheckedChange={(checked) => {
                            if (checked) setParentConcerns([...parentConcerns, concern]);
                            else setParentConcerns(parentConcerns.filter((c) => c !== concern));
                          }}
                        />
                        <Label htmlFor={`concern-${concern}`} className="text-sm cursor-pointer">{concern}</Label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>ปัญหาที่ผู้ปกครองเป็นกังวล อื่น ๆ</Label>
                  <Textarea value={parentConcernsOther} onChange={(e) => setParentConcernsOther(e.target.value)} rows={2} placeholder="ระบุปัญหาอื่น ๆ ที่ไม่มีในรายการ" />
                </div>
                <div className="space-y-2">
                  <Label>สิ่งที่ผู้ปกครองคาดหวังจากการทดลองเรียนครั้งนี้</Label>
                  <Textarea value={parentExpectationsTrial} onChange={(e) => setParentExpectationsTrial(e.target.value)} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>ความคาดหวังของผู้ปกครองในการให้น้องเรียนกับ Fishsix</Label>
                  <Textarea value={parentExpectationsFishsix} onChange={(e) => setParentExpectationsFishsix(e.target.value)} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>เกณฑ์ที่ใช้วัดว่าผลลัพธ์ที่ผู้ปกครองคาดหวังนั้นสำเร็จ</Label>
                  <Textarea value={successCriteria} onChange={(e) => setSuccessCriteria(e.target.value)} rows={3} placeholder="ระบุสิ่งที่คุณอยากเห็นหรือผลลัพธ์ที่ชัดเจน" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 3: ข้อมูลสำหรับ Sales */}
            <CollapsibleSection title="ข้อมูลสำหรับ Sales" defaultOpen={false} forceOpen={validationErrors.has('consultantTeacher')}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lead-id</Label>
                  <Input value={leadId} onChange={(e) => setLeadId(e.target.value)} placeholder="Lead_xxxxx" />
                </div>
                <div className="space-y-2">
                  <Label>คอร์สที่ต้องการลงเรียน</Label>
                  <Select value={desiredCourse} onValueChange={setDesiredCourse}>
                    <SelectTrigger><SelectValue placeholder="เลือกคอร์ส" /></SelectTrigger>
                    <SelectContent>
                      {COURSE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>จำนวนชั่วโมงที่ต้องการลงเรียน</Label>
                  <Input type="number" value={desiredHours} onChange={(e) => setDesiredHours(e.target.value ? Number(e.target.value) : '')} placeholder="ชั่วโมง" />
                </div>
                <div className="space-y-2">
                  <Label>คุณครูที่ให้คำปรึกษา <span className="text-red-500">*</span></Label>
                  <Input value={consultantTeacher} onChange={(e) => { setConsultantTeacher(e.target.value); clearError('consultantTeacher'); }} className={cn(validationErrors.has('consultantTeacher') && 'border-red-500')} />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>ข้อมูล Pain Point ของนักเรียนเพิ่มเติม</Label>
                  <Textarea value={additionalPainPoints} onChange={(e) => setAdditionalPainPoints(e.target.value)} rows={3} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 4: การติดตามผล */}
            <CollapsibleSection title="การติดตามผล" defaultOpen={false} forceOpen={Array.from(validationErrors).some(e => e.startsWith('followUp_'))}>
              <div className="space-y-4">
                {followUps.map((fu, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3 relative">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">การติดตามครั้งที่ {index + 1}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeFollowUp(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DatePickerField
                        label={<>วันที่ Follow <span className="text-red-500">*</span></>}
                        value={fu.followDate}
                        onChange={(v) => { updateFollowUp(index, 'followDate', v); clearError(`followUp_${index}_date`); }}
                        error={validationErrors.has(`followUp_${index}_date`)}
                      />
                      <div className="space-y-2">
                        <Label>ผู้รับผิดชอบ</Label>
                        <ReactSelect
                          options={employeeOptions}
                          value={fu.assignedEmployeeEmail ? employeeOptions.find(o => o.value === fu.assignedEmployeeEmail) || { value: fu.assignedEmployeeEmail, label: fu.assignedEmployeeEmail, email: fu.assignedEmployeeEmail } : null}
                          onChange={(opt) => updateFollowUp(index, 'assignedEmployeeEmail', opt?.value || '')}
                          onInputChange={(q) => { if (q.length >= 1) searchEmployees(q); }}
                          isLoading={isLoadingEmployees}
                          placeholder="ค้นหาพนักงาน..."
                          isClearable
                          noOptionsMessage={() => 'พิมพ์เพื่อค้นหา'}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>รายละเอียดการ Follow</Label>
                      <Textarea
                        value={fu.detail}
                        onChange={(e) => updateFollowUp(index, 'detail', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addFollowUp} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />เพิ่มการติดตาม
                </Button>
              </div>
            </CollapsibleSection>

            {/* Section 5: การประเมินผลโดยครู */}
            <CollapsibleSection title="การประเมินผลโดยครู" defaultOpen={false}>
              <div className="space-y-4">
                <div className="rounded-md border bg-muted/40 p-3 flex items-center justify-between text-sm">
                  <span className="font-medium">สิ่งที่ครูต้องรับทราบ</span>
                  {teacherAcknowledged ? (
                    <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-medium">ครูรับทราบแล้ว ✓</span>
                  ) : (
                    <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700 text-xs font-medium">ครูยังไม่ได้รับทราบ</span>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>คะแนนความเข้าใจในวิชานี้ก่อนเรียนที่น้องให้ตัวเอง พร้อมเหตุผล</Label>
                  <Textarea value={preScoreReason} onChange={(e) => setPreScoreReason(e.target.value)} rows={3} placeholder="เช่น 2/10 เพราะ น้องบอกว่าไม่รู้ว่าตอนไหนต้องใช้สูตรอะไร" />
                </div>
                <div className="space-y-2">
                  <Label>คะแนนความเข้าใจในวิชานี้หลังเรียนที่น้องให้ตัวเอง พร้อมเหตุผล</Label>
                  <p className="text-xs text-muted-foreground">(คะแนนต้องตั้งแต่ 8/10 ขึ้นไป ถ้าไม่ถึงครูต้องสอนจนทำให้น้องให้คะแนน 8/10 ให้ได้)</p>
                  <Textarea value={postScoreReason} onChange={(e) => setPostScoreReason(e.target.value)} rows={3} placeholder="เช่น 9/10 เพราะมองโจทย์ออกแล้ว ว่าต้องใช้สูตรอะไร ตอนไหน" />
                </div>
                <div className="space-y-2">
                  <Label>ภาพรวมการทดลองเรียนวันนี้</Label>
                  <Textarea value={trialOverview} onChange={(e) => setTrialOverview(e.target.value)} rows={3} placeholder="เช่น น้องมีการพูดคุย เปิดใจที่จะเรียนรู้ และตั้งใจเรียนเป็นอย่างดี" />
                </div>
                <div className="space-y-2">
                  <Label>จุดแข็งของน้อง</Label>
                  <Textarea value={studentStrengths} onChange={(e) => setStudentStrengths(e.target.value)} rows={3} placeholder="เช่น น้องมีพื้นฐานวิทยาศาสตร์ที่ดี สามารถตอบคำถามได้ถูกต้องและเข้าใจเนื้อหาอย่างรวดเร็ว" />
                </div>
                <div className="space-y-2">
                  <Label>จุดที่ต้องพัฒนา</Label>
                  <Textarea value={studentImprovements} onChange={(e) => setStudentImprovements(e.target.value)} rows={3} placeholder="เช่น ควรฝึกทำแบบฝึกหัดเพิ่มเติมเพื่อเสริมความมั่นใจในการประยุกต์ใช้ความรู้ รวมถึงฝึกการคำนวณเลขให้ไวขึ้น" />
                </div>
                <div className="space-y-2">
                  <Label>แนวทางที่ครูจะสอนน้อง</Label>
                  <Textarea value={teachingApproach} onChange={(e) => setTeachingApproach(e.target.value)} rows={3} placeholder="เช่น เน้นการสอนพื้นฐานให้แน่นและฝึกทักษะการวิเคราะห์โจทย์" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ระยะเวลาที่ควรเรียน</Label>
                    <Input value={recommendedDuration} onChange={(e) => setRecommendedDuration(e.target.value)} placeholder="เช่น เรียนสัปดาห์ละ 2 ครั้ง ครั้งละ 2 ชม. เป็นเวลา 3 เดือน หรือ 48 ชั่วโมง" />
                  </div>
                  <div className="space-y-2">
                    <Label>คอร์สที่เหมาะกับน้อง</Label>
                    <p className="text-xs text-muted-foreground">กรณีน้องมีพื้นฐานดีแต่ต้องเพิ่มความเข้าใจให้แนะนำ Private กรณีติวสอบ พื้นฐานอ่อนหรือเรียนเก่งมากให้แนะนำ Premiere</p>
                    <Select value={recommendedCourse} onValueChange={setRecommendedCourse}>
                      <SelectTrigger><SelectValue placeholder="เลือกคอร์ส" /></SelectTrigger>
                      <SelectContent>
                        {RECOMMENDED_COURSE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Section 6: การประเมินพฤติกรรมการสอน */}
            <CollapsibleSection title="การประเมินพฤติกรรมการสอน" defaultOpen={false}>
              <div className="space-y-6">
                {/* Part 1 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-foreground border-b pb-1">Part 1: การสอนของครูช่วยให้น้องเข้าใจบทเรียนหรือไม่?</h3>
                  <RatingField label="1.1 ครูอธิบายเนื้อหาอย่างชัดเจนและเป็นลำดับขั้นตอน" value={rating11} onChange={setRating11} />
                  <RatingField label="1.2 ครูใช้ตัวอย่างหรือสื่อช่วยสอนที่เหมาะสม" value={rating12} onChange={setRating12} />
                  <RatingField label="1.3 น้องสามารถตอบคำถามหรืออธิบายสิ่งที่เรียนได้อย่างมั่นใจ" value={rating13} onChange={setRating13} />
                </div>
                {/* Part 2 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-foreground border-b pb-1">Part 2: การสอนของครูช่วยให้น้องชอบบทเรียนหรือไม่?</h3>
                  <RatingField label="2.1 ครูสร้างบรรยากาศการเรียนที่สนุกและผ่อนคลาย" value={rating21} onChange={setRating21} />
                  <RatingField label="2.2 ครูมีการชื่นชมหรือสร้างแรงจูงใจให้น้อง" value={rating22} onChange={setRating22} />
                  <RatingField label="2.3 น้องแสดงความสนใจหรือมีส่วนร่วมในการเรียน" value={rating23} onChange={setRating23} />
                </div>
                {/* Part 3 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-foreground border-b pb-1">Part 3: การสอนของครูช่วยให้น้องอยากเรียนต่อหรือไม่?</h3>
                  <RatingField label="3.1 ครูมีการกระตุ้นให้น้องตั้งเป้าหมายการเรียนต่อ" value={rating31} onChange={setRating31} />
                  <RatingField label="3.2 น้องแสดงความกระตือรือร้นที่จะเรียนเนื้อหาต่อไป" value={rating32} onChange={setRating32} />
                  <RatingField label="3.3 น้องแสดงความมั่นใจที่จะกลับมาเรียนในครั้งหน้า" value={rating33} onChange={setRating33} />
                </div>
                {/* Part 4 */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-foreground border-b pb-1">Part 4: การสอนช่วยให้น้องทบทวนและยืนยันความเข้าใจได้หรือไม่?</h3>
                  <RatingField label="4.1 ครูเปิดโอกาสให้น้องอธิบายสิ่งที่เข้าใจหรือยกตัวอย่างกลับมา" value={rating41} onChange={setRating41} />
                  <RatingField label="4.2 ครูตอบคำถามและยืนยันความถูกต้องให้กับน้องเมื่อมีข้อสงสัย" value={rating42} onChange={setRating42} />
                  <RatingField label="4.3 น้องสามารถพูดทบทวนหรือเล่าเรื่องที่เกี่ยวข้องได้อย่างมั่นใจ" value={rating43} onChange={setRating43} />
                </div>
                {/* Total Score */}
                <div className="bg-muted/50 rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">คะแนนรวม Part 1-4</span>
                    <span className="text-lg font-bold text-primary">
                      {(() => {
                        const all = [rating11, rating12, rating13, rating21, rating22, rating23, rating31, rating32, rating33, rating41, rating42, rating43];
                        const filled = all.filter((v): v is number => v !== null);
                        if (filled.length === 0) return '—';
                        return `${filled.reduce((a, b) => a + b, 0)} / ${all.length * 5}`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Actions */}
            <div className="flex justify-end gap-3 pb-8">
              <Button variant="outline" onClick={() => navigate('/admin/class-trials')}>ยกเลิก</Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />กำลังบันทึก...</> : <><Save className="h-4 w-4 mr-2" />บันทึก</>}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
